import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  linkedSignal,
  output,
  Signal,
  signal,
  TemplateRef,
  effect,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { PageEvent } from '@angular/material/paginator';
import { FormArray, FormGroup } from '@angular/forms';
import { MatSort, Sort } from '@angular/material/sort';

import {
  GridColumn,
  GridColumns,
  GridFeatures,
  GridSort,
  GridViews,
  mapGridColumns,
  resetSort,
  WcgAdvancedFilter,
  WcgChipType,
  WcgTableService,
} from '@wcg/frontend-framework';

import {
  WcgFilterIndicatorsOptions,
  WcgTableValues,
} from '@shared/interfaces/wcg-table-utils.interface';
import {
  CONFIG_WRAPPER_IMPORTS,
  getSortedColumn,
  GridComponentType,
  GridPageView,
  ToggleGroupOptions,
  updateGridFilter,
} from '@data-access/shared';
import type { ExportArgs } from '@data-access/downloads-exports';
import {
  GRID_SEARCH_FORM,
  GridSearchForm,
  GridType,
} from '@data-access/grid-views';
import {
  flattenAdvancedFilters,
  GRID_ADVANCE_FILTERS_FORM,
} from '@data-access/advanced-filters';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { skip } from 'rxjs';

type GridRecordRow = { id: string | null; need_to_upload?: boolean };
type GridRecordState = { single: boolean; id: string | null };

/**
 * Grid Page Container Component
 * This component is used to wrap the grid page content with title and body sections.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'wcg-grid-page-container',
  template: `
    <div class="w-full flex flex-col gap-4">
      <div>
        <ng-content select="[titleContent]" />
      </div>
      <div class="w-full">
        <ng-content select="[bodyContent]" />
      </div>
    </div>
  `,
})
export class GridPageContainerComponent {}
/**
 * Grid Page Container Component
 * This component is used to wrap the grid page content with title and body sections.
 */

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CONFIG_WRAPPER_IMPORTS],
  selector: 'wcg-ui-grid-wrapper',
  styleUrl: './ui-grid-wrapper.component.css',
  templateUrl: './ui-grid-wrapper.component.html',
  providers: [WcgTableService],
})
export class UiGridWrapperComponent<T> {
  private readonly _destroyRef = inject(DestroyRef);
  public readonly advancedFiltersFormGroup = inject<FormGroup>(
    GRID_ADVANCE_FILTERS_FORM,
  );
  private readonly _wcgTableService = inject(WcgTableService);
  public readonly searchFieldForm =
    inject<FormGroup<GridSearchForm>>(GRID_SEARCH_FORM);
  private readonly _currentPage = signal<number>(1);
  private readonly _currentSort = signal<Sort>({ active: '', direction: '' });
  readonly $resetSort = signal<resetSort>({} as resetSort);
  protected readonly gridComponentType = GridComponentType;
  protected readonly wcgChipType = WcgChipType;
  /*
   * @DATA INPUT SIGNALS
   * */
  readonly advancedFilters = input<WcgAdvancedFilter>();
  readonly apiURL = input<string>('');
  readonly disableDragAndDrop = input<boolean>(false);
  readonly gridColumns = input({} as GridColumns);
  readonly gridData = input<T[]>();
  readonly gridType = input.required<GridType>();
  readonly isLoading = input<boolean>(true);
  readonly noDataMessage = input<string>('The table is EMPTY');
  readonly paginationOptions = input<number[]>();
  readonly totalCount = input<number>(0);
  readonly wcgTableValues = input<WcgTableValues>({} as WcgTableValues);
  readonly gridFeatures = input<GridFeatures>({} as GridFeatures);
  readonly pageView = input<GridPageView>({ page: 1, pageSize: 10 });
  readonly searchString = input<string>('');
  readonly enableSearch = input<boolean>(true);
  readonly enableManageColumn = input<boolean>(true);
  readonly enableAdvanceFilters = input<boolean>(true);
  readonly enableGridView = input<boolean>(true);
  readonly selectedRows = input<T[]>([]);
  readonly canBulkEdit = input<boolean>(false);
  readonly deleteBtnLabel = input<string>('Delete');
  readonly canBulkDelete = input<boolean>(false);
  readonly studyId = input<string>('');
  readonly entityId = input<string>('');
  readonly entityType = input<string>('');
  readonly isOutcome = input<boolean>(false);
  readonly exportArgs = input<ExportArgs | undefined>(undefined);
  readonly enableSegmentedToggle = input<boolean>(false);
  readonly toggleGroupOptions = input<ToggleGroupOptions[]>([]);
  readonly selectedToggle = input<string>('');
  readonly excludeNeedToUpload = input<boolean>(false);

  readonly advanceFiltersLength = signal<number>(0);
  readonly appliedFilters = linkedSignal({
    computation: (filters) => {
      if (!Object.values(filters!).length) {
        return {} as WcgAdvancedFilter;
      }
      return filters;
    },
    source: this.advancedFilters,
  });

  readonly $gridRecordState = computed<GridRecordState>(() => {
    const data = (this.gridData() ?? []) as GridRecordRow[];
    const excludeNeedToUpload = this.excludeNeedToUpload();

    // Exclude placeholder rows (need_to_upload: true) so a placeholder-only grid
    // never triggers the single-file download shortcut in grid-actions.
    const rowData = excludeNeedToUpload
      ? data.filter((row) => !row.need_to_upload)
      : data;

    const isSingle = excludeNeedToUpload
      ? rowData.length === 1 && this.pageView().page === 1
      : this.totalCount() === 1;

    return isSingle
      ? { single: true, id: rowData[0].id }
      : { single: false, id: null };
  });
  /*
   * @DATA INPUT SIGNALS END
   * */
  /*
   * @TEMPLATE_REF INPUT SIGNALS
   * */
  readonly actionHeaderTemplateRef = input<TemplateRef<unknown>>();
  readonly actionTemplateRef = input<TemplateRef<unknown>>();
  readonly addressesTemplateRef = input<TemplateRef<unknown>>();
  readonly tasksTemplateRef = input<TemplateRef<unknown>>();
  readonly checkBoxReadonlyTemplateRef = input<TemplateRef<unknown>>();
  readonly checkBoxTemplateRef = input<TemplateRef<unknown>>();
  readonly chipTemplateRef = input<TemplateRef<unknown>>();
  readonly gridViewsTemplateRef = input<TemplateRef<unknown>>();
  readonly gridFeatureTemplateRef = input<TemplateRef<unknown>>();
  readonly textWithIconTemplateRef = input<TemplateRef<unknown>>();
  /**
   * Optional quick-filter chips rendered inline at the start of the advanced
   * filters row. Renders only when a template is supplied, so grids that do not
   * pass one are unaffected.
   */
  readonly quickFiltersTemplateRef = input<TemplateRef<unknown>>();
  readonly selectTemplateRef = input<TemplateRef<unknown>>();
  readonly bulkActionsTemplateRef = input<TemplateRef<unknown>>();
  readonly enableDynamicFilterComponent = input<boolean>(false);

  readonly enableFilterComponent = linkedSignal({
    computation: (enableDynamic) => {
      return !enableDynamic;
    },
    source: this.enableDynamicFilterComponent,
  });
  /*
   * @TEMPLATE_REF INPUT SIGNALS END
   * */

  /*
   * @DATA OUTPUT SIGNALS
   * */
  clearAdvancedFilters = output<boolean>();
  handleAdvancedFilters = output<WcgAdvancedFilter[]>();
  handlePagination = output<PageEvent>();
  handleSearch = output<string>();
  handleSortChange = output<Sort>();
  manageColumns = output<void>();
  removeAdvancedFilters = output<WcgFilterIndicatorsOptions>();
  updateColumns = output<GridColumns>();
  resetSelectedRows = output<void>();
  delete = output<void>();
  bulkEdit = output<void>();
  selectToggle = output<string>();
  downloadSingleFile = output<{ id: string }>();
  /*
   * @DATA OUTPUT SIGNALS END
   * */

  readonly gColumns = linkedSignal({
    computation: (columns) => {
      const _columnKeys = Object.keys(columns);
      if (!_columnKeys?.length) {
        return [];
      }
      return _columnKeys;
    },
    source: this.gridColumns,
  });
  readonly gridViewData: Signal<GridViews> = computed(() => ({
    advancedFilters: this.advancedFilters()!,
    columns: this.gridColumns(),
    default: false,
    gridType: this.gridType(),
    id: '',
    name: '',
    pagination: this._currentPage(),
    searchString: this.searchString(),
    sorted: this._currentSort().direction as GridSort,
  }));

  constructor() {
    this._customListener();
  }

  removeFilter(event: WcgFilterIndicatorsOptions) {
    this.appliedFilters.update(
      (_filters) =>
        flattenAdvancedFilters(updateGridFilter(event, _filters!).filters) ??
        {},
    );
    if ('name' in event) {
      this.searchFieldForm.get('searchString')?.reset('');
    }
    this.removeAdvancedFilters.emit(event);
  }

  clearFilters() {
    this.advancedFiltersFormGroup.reset();
    this.clearAdvancedFilters.emit(true);
  }

  applyFilters(event: WcgAdvancedFilter[]) {
    this.handleAdvancedFilters.emit(event);
  }

  handleDropChange(event: CdkDragDrop<GridColumns>) {
    this.gColumns.update((_columns) => {
      const _cols = [..._columns];
      moveItemInArray(_cols, event.previousIndex, event.currentIndex);
      return [..._cols];
    });
    const _gridColumns: GridColumn[] = [];
    for (const column of this.gColumns()) {
      _gridColumns.push(this.gridColumns()[column]);
    }
    this.updateColumns.emit(mapGridColumns(_gridColumns));
  }

  handleSearchEvent(e?: Event): void {
    if (e) {
      e.preventDefault();
    }

    this.handleSearch.emit(
      this.searchFieldForm.get('searchString')?.value || '',
    );
    this.searchFieldForm.get('searchString')?.reset('');
  }

  handleSortEvent(event: Sort): void {
    const nextSort: Sort = {
      active: event.active || '',
      direction: event.direction || '',
    };

    if (this._isSortChanged(this._currentSort(), nextSort)) {
      this._currentSort.set(nextSort);
      this.handleSortChange.emit(nextSort);
    }
  }

  handlePageEvent($event: PageEvent): void {
    this._currentPage.set($event.pageIndex);
    this.handlePagination.emit($event);
  }

  onRevertGridState() {
    this.searchFieldForm.get('searchString')?.reset('');
    const _sort: Sort = {
      active: '',
      direction: '',
    };
    this._currentSort.set(_sort);
    this.$resetSort.set({
      reset: true,
      sort: _sort as MatSort,
    });
  }

  private _isSortChanged(current: Sort, next: Sort): boolean {
    return (
      current.active !== next.active || current.direction !== next.direction
    );
  }

  private _customListener() {
    toObservable(this.searchString)
      .pipe(skip(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (val: string) => {
          if (
            val.toLowerCase() !==
            this.searchFieldForm.get('searchString')?.value?.toLowerCase()
          ) {
            this.searchFieldForm
              .get('searchString')
              ?.setValue(val, { emitEvent: false });
          }
        },
      });

    toObservable(this.gridColumns)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((columns) => {
        const { sortByField, sortByOrder } = getSortedColumn(columns);

        const nextSort: Sort = {
          active: sortByField,
          direction: sortByOrder,
        };

        if (this._isSortChanged(this._currentSort(), nextSort)) {
          this._currentSort.set(nextSort);
          this.$resetSort.set({
            reset: true,
            sort: nextSort as MatSort,
          });
        }
      });

    this._registerDynamicFilterListener();
  }

  private _registerDynamicFilterListener(): void {
    effect(() => {
      if (!this.enableDynamicFilterComponent()) {
        return;
      }

      const filters = this.advancedFilters();

      if (!filters || !Object.keys(filters).length) {
        return;
      }

      const currentLength = Object.keys(filters).length;

      if (this.advanceFiltersLength() === currentLength) {
        return;
      }

      this.advanceFiltersLength.set(currentLength);

      const filtersArray = this.advancedFiltersFormGroup.get(
        'filters',
      ) as FormArray;

      // Reset the form array
      filtersArray.clear();

      // Re-initialize filter component
      this.enableFilterComponent.set(false);
      setTimeout(() => {
        this.enableFilterComponent.set(true);
      }, 0);
    });
  }
}
